import React from 'react';
import { MoreVertical, Star } from 'lucide-react';

export default function ConversationCard({
  conversation,
  isSelected,
  onClick,
  menuOpen,
  onMenuToggle,
  onMenuClose,
  menuItems = [],
}) {
  const {
    conversation_id,
    display_name,
    photo_url,
    last_message_preview,
    last_message_at,
    unread_count,
    starred,
  } = conversation || {};

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    if (diff < 24 * 60 * 60 * 1000) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (diff < 7 * 24 * 60 * 60 * 1000) return d.toLocaleDateString([], { weekday: 'short' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const initials = display_name
    ? display_name.trim().split(/\s+/).map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(conversation)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick?.(conversation)}
      className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors flex items-start gap-3 relative ${
        isSelected ? 'bg-blue-50 border-l-4 border-l-primary-600' : ''
      }`}
    >
      <div className="flex-shrink-0 relative">
        {photo_url ? (
          <img src={photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center">
            <span className="text-gray-600 font-semibold text-sm">{initials}</span>
          </div>
        )}
        {unread_count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-primary-500" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className={`font-semibold text-sm truncate ${unread_count > 0 ? 'text-gray-900' : 'text-gray-800'}`}>
            {display_name || 'Unknown'}
          </p>
          <div className="flex items-center gap-1 flex-shrink-0">
            {starred && <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />}
            <span className="text-xs text-gray-500">{formatDate(last_message_at)}</span>
            {onMenuToggle && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onMenuToggle();
                }}
                className="p-1 hover:bg-gray-100 rounded"
                aria-label="Conversation actions"
              >
                <MoreVertical className="w-4 h-4 text-gray-500" />
              </button>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-600 truncate mt-0.5">
          {last_message_preview || 'No messages yet'}
        </p>
      </div>
      {menuOpen && menuItems.length > 0 && (
        <>
          <div
            className="fixed inset-0 z-10"
            aria-hidden="true"
            onClick={(e) => {
              e.stopPropagation();
              onMenuClose?.();
            }}
          />
          <div
            className="absolute right-3 top-12 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-[180px]"
            onClick={(e) => e.stopPropagation()}
          >
            {menuItems.map((item) => (
              <button
                key={item.label}
                type="button"
                onClick={() => item.onClick?.()}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                  item.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
